import { saveAs } from 'file-saver';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { KeplerMatchList } from '../../models/KeplerMatch';
import { KeplerService } from '../../services/kepler.service';
import { JobsService, ModalComponent, PopUpService, ProfileService, StoreService, ToastAlertService } from 'core/index';
import { ConfigurationsService, LocalesService } from 'core/services';
import { Decorator, DynamicFormComponent, NumberQuestion, QuestionBase } from 'core/components';
import { KeplerGoalListService } from './kepler-goal-list.service';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';

@Component({
  selector: 'lms-kepler-goal-list',
  templateUrl: './kepler-goal-list.component.html',
  styleUrls: ['./kepler-goal-list.component.scss'],
  providers: [KeplerGoalListService]
})
export class KeplerGoalListComponent implements OnInit, OnDestroy {
  @Input() match: any;
  @Input() listOptions: any;
  @Output() uploadKpis = new EventEmitter<any>();
  @ViewChild('modalCsvUpload', { static: false }) public modalCsvUpload: ModalComponent;
  @ViewChild('confirmKpiModal', { static: false }) public confirmKpiModal: ModalComponent;
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;
  public didMatchEnd: boolean;
  public didMatchStart: boolean;
  public csvData: File;
  public enabledLastRoundGoal: boolean = false;
  public hasItems = false;
  public userRole: any;
  public dataSource: any;
  public toolbarConfig: any;
  public tooltipTextLastRound: any;
  private jobSubscription: Subscription;
  public clientKeplerGoalListId = 'CLIENT_KEPLER_GOAL_LIST';
  public goalsKeplerList: KeplerMatchList;
  public keplerGoalSubscription: Subscription = new Subscription();
  public objectivesDuration: string;
  public limitQuestions: QuestionBase<any>[] = [];
  constructor(
    public keplerService: KeplerService,
    public keplerGoalListService: KeplerGoalListService,
    public profileService: ProfileService,
    public jobsService: JobsService,
    public translate: TranslateService,
    public popupService: PopUpService,
    private configurationsService: ConfigurationsService,
    private localesService: LocalesService,
    public toastService: ToastAlertService
  ) {}

  async ngOnInit() {
    this.toolbarConfig = {
      list: {
        listId: this.clientKeplerGoalListId,
        download: true,
        hidePrint: false,
        filterRow: false,
        columnChooser: false,
        hideSearch: true,
        hideItemsSelector: false,
        skeletor: true
      }
    };
    this.didMatchEnd = this.match.hasLastRoundEnded || new Date(this.match.endDate) < new Date();
    this.isMatchStart(this.match.timezone, this.match.startDate);
    this.keplerGoalSubscription = this.getLoggedUserRole$()
      .pipe()
      .subscribe(async () => {
        this.dataSource = await this.keplerGoalListService.getDatasource(
          this.clientKeplerGoalListId,
          this.match,
          this.listOptions
        );
      });

    this.tooltipTextLastRound = await this.getInfoText('kepler.goal.last.round');
    this.objectivesDuration = this.match.shouldDisplayObjectives ? this.match.defaultObjectivesDuration : '';
  }

  async ngOnChanges() {
    this.dataSource = await this.keplerGoalListService.getDatasource(
      this.clientKeplerGoalListId,
      this.match,
      this.listOptions
    );
    if (this.dataSource?.docs?.length) this.hasItems = true;
  }

  /**
   * Set the data of the table
   * @param dataSource
   */
  public setDatasourceUpdate(dataSource: any): void {
    this.dataSource = dataSource;
    StoreService.update(this.clientKeplerGoalListId, this.dataSource);
  }

  /**
   * Get the role of the logged user
   * @private
   */
  private getLoggedUserRole$(): Observable<any> {
    return this.profileService.getLoggedUserRole$().pipe(
      tap((userRole) => {
        this.userRole = userRole;
      })
    );
  }

  public ngOnDestroy(): void {
    this.keplerGoalSubscription.unsubscribe();
    this.jobSubscription ? this.jobSubscription.unsubscribe() : null;
  }

  isMatchStart(timezone, startDate) {
    const startMatchDate = DateTime.fromJSDate(new Date(startDate)).setZone(timezone).toISO();
    const currentDate = DateTime.fromJSDate(new Date()).setZone(timezone).toISO();
    this.didMatchStart = startMatchDate <= currentDate;
  }

  getObjectivesDeadLineDays() {
    const startDate = new Date().getTime();
    const endDate = new Date(this.match.currentObjectivesDeadline).getTime();
    const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
    return days;
  }

  getObjectivesDeadLine(options) {
    const { days, date } = options;
    if (date) {
      return DateTime.fromJSDate(new Date(date)).setZone(this.match.timezone).toFormat('dd/MM/yyyy');
    }
    const currentDate = new Date();
    const matchStartDate = new Date(this.match.startDate);
    const resultDate = currentDate >= matchStartDate ? currentDate : matchStartDate;
    let deadlineDate = DateTime.fromJSDate(resultDate)
      .setZone(this.match.timezone)
      .set({ hour: 23, minute: 59 })
      .plus({ days: days })
      .toISO();
    deadlineDate = DateTime.fromJSDate(new Date(deadlineDate)).toFormat('dd/MM/yyyy');
    return deadlineDate;
  }

  private getKpiLimitQuestion() {
    const textDecorator = new Decorator({
      html: `<div class="gl-popup__text padding-none decorator"><b>${this.translate.instant(
        'kepler.kpiConfirmMessageLimit',
        {
          date: this.getObjectivesDeadLine({ days: this.objectivesDuration })
        }
      )}</b></div>`
    });
    this.limitQuestions = [
      new NumberQuestion({
        key: 'defaultObjectivesDuration',
        label: 'kepler.info.kpiLimitMessage',
        class: 'col-1',
        tooltip: 'kepler.info.kpiLimitTooltip',
        placeholder: 'kepler.info.kpiLimitPlaceholder',
        value: this.objectivesDuration,
        validators: {
          required: true,
          minValue: { limit: 1 }
        },
        eventHandlers: {
          onChange: (value) => {
            this.objectivesDuration = value;
            textDecorator.html = `<div class="gl-popup__text padding-none decorator"><b>${this.translate.instant(
              'kepler.kpiConfirmMessageLimit',
              {
                date: this.getObjectivesDeadLine({ days: value })
              }
            )}</b></div>`;
          }
        }
      }),
      textDecorator
    ];
  }

  confirmModal() {
    this.keplerService
      .uploadKpisCsv(
        this.csvData,
        this.match.id,
        this.match.clientId,
        this.enabledLastRoundGoal,
        this.objectivesDuration
      )
      .then((response: any) => {
        this.manageBulkJob(response, this.jobsService.jobTypes.UPLOAD_KPI_TEMPLATES);
      })
      .catch((err) => {
        this.showToastAlert(err);
      });
    this.closeConfirmKpiModal();
  }

  closeConfirmKpiModal() {
    this.confirmKpiModal.close();
    this.enabledLastRoundGoal = false;
    this.objectivesDuration = this.match.shouldDisplayObjectives ? this.match.defaultObjectivesDuration : '';
  }

  openConfirmModal() {
    this.confirmKpiModal.open();
    this.getKpiLimitQuestion();
  }

  openCsvModal() {
    this.enabledLastRoundGoal = false;
    if (this.csvData) {
      this.csvData = null;
    }
    this.modalCsvUpload.open();
  }

  closeCsvModal() {
    this.modalCsvUpload.close();
    if (this.csvData) {
      this.openConfirmModal();
    } else {
      this.enabledLastRoundGoal = false;
    }
  }

  onSaveCsv(csvData: any) {
    this.csvData = csvData;
  }

  showToastAlert(err) {
    this.toastService
      .showConfirm({
        type: 'toast',
        title: this.translate.instant('nouns.csvErrorFileTitle'),
        text: this.translate.instant('nouns.csvErrorFileMessage'),
        actionButtonText: this.translate.instant('nouns.download')
      })
      .then(() => this.downloadCsv());
  }

  downloadCsv() {
    this.keplerService.getRoundCsvTemplate(this.match.id, this.enabledLastRoundGoal).subscribe({
      next: (response) => {
        const contentDisposition = response.headers.get('content-disposition') || '';
        const matches = /filename=([^;]+)/gi.exec(contentDisposition);
        const fileName = (matches?.length ? matches[1].slice(1, -1) : `KeplerKPIsTemplate_${this.match.id}.csv`).trim();
        saveAs(response.body, fileName);
      },
      error: (error) => {
        this.popupService.showError({
          title: 'Error',
          text: error.serverMessage
        });
      }
    });
  }

  manageBulkJob(enrollmentJob, type) {
    this.jobSubscription = this.jobsService.manageJob(enrollmentJob.jobId, type).subscribe((job) => {
      if (job.status === 'finished' || job.status === 'fatal-error') {
        this.keplerService.getKeplerMatch$(this.match.id).subscribe(async (match) => {
          this.uploadKpis.emit(match);
          this.dataSource = await this.keplerGoalListService.getDatasource(
            this.clientKeplerGoalListId,
            match,
            this.listOptions
          );
        });
        this.jobSubscription.unsubscribe();
      }
    });
  }

  lastRoundGoal(event) {
    this.enabledLastRoundGoal = event.detail.value;
  }

  /**
   * Get the text from contentful/configurations
   * @param key
   * @private
   */
  private async getInfoText(key: string): Promise<any> {
    return lastValueFrom(
      this.configurationsService.getInfoMessage$(
        'toolTip',
        'fields.keys',
        key,
        'textoDeAyuda',
        this.localesService.getBrowserLocale(),
        true
      )
    );
  }
}
